import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

// Import components
import Home from './components/Home';
import LocationConfirm from './components/LocationConfirm';
import InterestSelect from './components/InterestSelect';
import PlaceSelection from './components/PlaceSelection';
import MyInterests from './components/MyInterests';
import Account from './components/Account';
import GeneratingTour from './components/GeneratingTour';
import Tour from './components/Tour';
import PastTours from './components/PastTours';
import BuyCredits from './components/BuyCredits';
import PaymentCallback from './components/PaymentCallback';
import Login from './components/Login';
import ScrollToTop from './components/ScrollToTop';
import PendingTourNotification from './components/PendingTourNotification';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import RefundPolicy from './components/RefundPolicy';
import FAQ from './components/FAQ';
import CommunityToursMap from './components/CommunityToursMap';
import MyToursMap from './components/MyToursMap';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { DistanceUnitProvider } from './contexts/DistanceUnitContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import { PostHogProvider } from './contexts/PostHogContext';
import { checkCredits } from './services/creditsService';

function ThemeColorUpdater() {
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const color = isDarkMode ? '#1A1A1A' : '#FFFFFF';
    let metaTag = document.querySelector('meta[name="theme-color"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('name', 'theme-color');
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', color);
  }, [isDarkMode]);

  return null;
}

// Protected route wrapper (no LocationProvider here)
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--background)',
        color: 'var(--text-primary)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-color)',
            borderTop: '3px solid var(--primary-color)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirects to home if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--background)',
        color: 'var(--text-primary)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-color)',
            borderTop: '3px solid var(--primary-color)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user, profile, profileLoading } = useAuth();
  const [credits, setCredits] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingCredits, setLoadingCredits] = useState(true);

  // Fetch user's credits on mount and when user changes
  useEffect(() => {
    const fetchCredits = async () => {
      // Don't fetch credits if no user
      if (!user) {
        setCredits(0);
        setLoadingCredits(false);
        return;
      }

      try {
        setLoadingCredits(true);
        console.log('💰 Fetching credits for authenticated user:', user.id);
        const creditsData = await checkCredits(user.id);
        setCredits(creditsData.total_credits);
      } catch (error) {
        console.error('Error fetching credits:', error);
        setCredits(0);
      } finally {
        setLoadingCredits(false);
      }
    };

    fetchCredits();
  }, [user]);

  // Function to refresh credits (can be called after purchases)
  const refreshCredits = async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Refreshing credits for authenticated user:', user.id);
      const creditsData = await checkCredits(user.id);
      setCredits(creditsData.total_credits);
    } catch (error) {
      console.error('Error refreshing credits:', error);
    }
  };

  // Expose refreshCredits globally for use in other components
  useEffect(() => {
    (window as any).refreshCredits = refreshCredits;
    return () => {
      delete (window as any).refreshCredits;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  
  return (
    <Routes>
      {/* Login page is now the home page */}
      <Route path="/" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      {/* Home/Browse route - accessible to everyone, but with different UI for logged in vs not logged in */}
      <Route path="/home" element={
        <Home 
          userName={profile?.full_name || user?.email?.split('@')[0] || 'User'} 
          credits={credits} 
        />
      } />

      {/* Legal pages - accessible to everyone */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/faq" element={<FAQ />} />
      
      {/* Community Tours Map - protected route for logged-in users only */}
      <Route path="/community-tours-map" element={
        <ProtectedRoute>
          <CommunityToursMap />
        </ProtectedRoute>
      } />
      
      {/* My Tours Map - protected route for logged-in users only */}
      <Route path="/my-tours-map" element={
        <ProtectedRoute>
          <MyToursMap />
        </ProtectedRoute>
      } />
      
      {/* Protected routes - LocationProvider is inside ProtectedRoute */}
      <Route path="/location" element={
        <ProtectedRoute>
          <LocationConfirm />
        </ProtectedRoute>
      } />
      <Route path="/interests" element={
        <ProtectedRoute>
          <InterestSelect />
        </ProtectedRoute>
      } />
      <Route path="/places" element={
        <ProtectedRoute>
          <PlaceSelection />
        </ProtectedRoute>
      } />
      <Route path="/my-interests" element={
        <ProtectedRoute>
          <MyInterests />
        </ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute>
          <Account />
        </ProtectedRoute>
      } />
      <Route path="/generating" element={
        <ProtectedRoute>
          <GeneratingTour />
        </ProtectedRoute>
      } />
      <Route path="/tour" element={
        <Tour />
      } />
      <Route path="/tour/:id" element={
        <Tour />
      } />
      <Route path="/past-tours" element={
        <ProtectedRoute>
          <PastTours />
        </ProtectedRoute>
      } />
      <Route path="/buy-credits" element={
        <ProtectedRoute>
          <BuyCredits />
        </ProtectedRoute>
      } />
      <Route path="/payment-callback" element={
        <ProtectedRoute>
          <PaymentCallback />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/home" />} />
    </Routes>
  );
}

// Always provide LocationProvider (needed for browsing tours, even when not logged in)
function ConditionalLocationProvider({ children }: { children: React.ReactNode }) {
  return <LocationProvider>{children}</LocationProvider>;
}

function App() {
  // Disable right-click context menu on desktop
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <PostHogProvider>
          <DistanceUnitProvider>
            <ThemeColorUpdater />
            <Router>
              <ConditionalLocationProvider>
                <ScrollToTop />
                <AppRoutes />
                <PendingTourNotification />
              </ConditionalLocationProvider>
            </Router>
          </DistanceUnitProvider>
          </PostHogProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
